import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type ReverseMode, reverseText } from "../lib/reverse";

const SAMPLE = "Hello World";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function TextReverseTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [mode, setMode] = useState<ReverseMode>("characters");
  const [copied, setCopied] = useState(false);

  const output = useMemo(() => reverseText(input, mode), [input, mode]);

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="sm:max-w-xs">
        <Label htmlFor={`${uid}-mode`}>{m.rv_mode()}</Label>
        <select
          id={`${uid}-mode`}
          className={`mt-1.5 ${selectClass}`}
          value={mode}
          onChange={(e) => setMode(e.target.value as ReverseMode)}
        >
          <option value="characters">{m.rv_mode_chars()}</option>
          <option value="words">{m.rv_mode_words()}</option>
          <option value="lines">{m.rv_mode_lines()}</option>
        </select>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.rv_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.rv_placeholder()}
            spellCheck={false}
            className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`${uid}-out`}>{m.rv_output_label()}</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              disabled={!output}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.rv_copied() : m.rv_copy()}
            </Button>
          </div>
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="rv-output"
            value={output}
            spellCheck={false}
            className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );
}
